'use client'

import { AddToCartButton } from '@/AddToCartButton'
import { Button } from '@/General/Button'
import { useSubscriptionEligibility } from '@/useSubscriptionEligibility'
import { useStorefrontDisplayCurrency } from '@/useStorefrontDisplayCurrency'
import {
  MASTERCLASS_TRIAL_PRODUCT_ID,
  MASTERCLASS_SUBSCRIPTION_PRODUCT_ID,
} from '@/productIds'

interface MasterclassButtonProps {
  className?: string
  variant?: 'default' | 'large' | 'product'
}

export const MasterclassButton = ({
  className,
  variant = 'default',
}: MasterclassButtonProps) => {
  const {
    isSubscriber,
    hasHadSubscriptionBefore,
    isEligibleForTrial,
    isLoading,
    isLoggedIn,
  } = useSubscriptionEligibility()
  const { currencySymbol } = useStorefrontDisplayCurrency()

  // 1. If customer is a subscriber, show "Go to your account"
  if (isSubscriber) {
    return (
      <Button href="/account/masterclass" className={className}>
        Go to your account
      </Button>
    )
  }

  // Show loading state only if query is actually loading (user is logged in and query is running)
  if (isLoading && isLoggedIn) {
    return (
      <Button className={className} disabled>
        Loading...
      </Button>
    )
  }

  // 2. If customer has had subscription before and is not eligible for trial,
  // show signup with $50/$50 (no trial)
  // Remove isLoggedIn check - hasHadSubscriptionBefore can only be true if query ran (user logged in)
  if (hasHadSubscriptionBefore && !isEligibleForTrial) {
    const subscriptionOption = {
      id: MASTERCLASS_SUBSCRIPTION_PRODUCT_ID,
      description: 'Monthly Subscription',
      price: 50,
      currencySymbol,
      inStock: true,
      productType: '',
      needsShipping: false,
    }

    const subscribeLabel =
      variant === 'product'
        ? `Subscribe now (${currencySymbol}50)`
        : `Subscribe now (${currencySymbol}50 / month)`

    return (
      <AddToCartButton
        productId={MASTERCLASS_SUBSCRIPTION_PRODUCT_ID}
        productName="Vanishing Inc. Masterclass"
        options={[subscriptionOption]}
        showOptions={false}
        showEmailNotification={false}
        className={className}
      >
        {subscribeLabel}
      </AddToCartButton>
    )
  }

  // 3. Otherwise, show £1 / $1 trial (default for new users or when not logged in)
  // This is the fallback that should always render
  return (
    <AddToCartButton
      productId={MASTERCLASS_TRIAL_PRODUCT_ID}
      productName="Vanishing Inc. Masterclass"
      options={[]}
      showOptions={false}
      showEmailNotification={false}
      className={className}
    >
      {variant === 'large'
        ? `Subscribe Now - Try for ${currencySymbol}1`
        : variant === 'product'
          ? `Start ${currencySymbol}1 Masterclass trial`
          : `Try Masterclass for ${currencySymbol}1`}
    </AddToCartButton>
  )
}
